import { CheckmarkOutline18Icon } from "@vygruppen/spor-icon-react";
import { useApiAction } from "api/drops/useApiAction";
import {
  initiatePhoneCall,
  performPuzzelCatalogSearch,
} from "api/puzzel/puzzel";
import debounce from "debounce";
import { CallOutStyled } from "features/Header/PuzzelTelephone/PuzzelStyle";
import { CenteredLabelInput } from "features/Header/PuzzelTelephone/components/Logon/StyledLogonComponents";
import {
  CatalogContactType,
  UserStatus,
} from "features/Header/PuzzelTelephone/components/types";
import { outboundPhoneNumber } from "features/Header/PuzzelTelephone/outboundPhoneNumber";
import { usePuzzelExtendedStateInformation } from "features/Header/PuzzelTelephone/provider/contexts/ListenForCallStatusContext";
import { usePuzzelAuthenticationContext } from "features/Header/PuzzelTelephone/provider/contexts/PuzzelAuthenticationContext";
import { useUserDetailsState } from "features/Header/PuzzelTelephone/provider/contexts/UserDetailsAndLogOnOffContext";
import { useUserValidationState } from "features/Header/PuzzelTelephone/provider/contexts/UserValidationContext";
import { validPhoneNumber } from "features/Header/PuzzelTelephone/utils";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import { KeyboardEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, AutocompleteItem } from "shared/components/Spor";
import { useInfiniteScroll } from "shared/hooks/useInfiniteScroll";
import { useOnOutsideMouseclick } from "shared/hooks/useOnOutsideMouseclick";
import { usePuzzelCurrentlyCalling } from "stores/usePuzzleCurrentlyCalling";
import { useTheme } from "styled-components";

export type ContactInfo = {
  readonly phoneNumber: string | undefined;
  readonly name: string;
};

type SearchItem = {
  value: string;
  label: string;
  disabled: boolean;
  name: string;
  phoneNumber: string | undefined;
};

const compareNames = (contactA: ContactInfo, contactB: ContactInfo) => {
  if (contactA.name.toUpperCase() < contactB.name.toUpperCase()) {
    return -1;
  }
  if (contactA.name.toUpperCase() > contactB.name.toUpperCase()) {
    return 1;
  }
  return 0;
};

const assembleSearchResults = (
  catalogSearchResult: CatalogContactType[],
): ContactInfo[] =>
  catalogSearchResult
    .flatMap((contact: CatalogContactType): ContactInfo[] => {
      const fullName = `${contact.firstName} ${contact.lastName}`.trim();
      const telephoneNumber = contact.communicationPoints.find(
        (communicationPoint: any) =>
          communicationPoint.communicationType === "Phone",
      )?.remoteAddress;
      const cellphoneNumber = contact.communicationPoints.find(
        (communicationPoint: any) =>
          communicationPoint.communicationType === "Cell",
      )?.remoteAddress;
      const contactPoints = [
        { name: fullName, phoneNumber: telephoneNumber },
        { name: fullName, phoneNumber: cellphoneNumber },
      ];
      if (
        contactPoints.some((contactPoint) =>
          validPhoneNumber(contactPoint.phoneNumber),
        )
      ) {
        return contactPoints.filter((contactPoint) =>
          validPhoneNumber(contactPoint.phoneNumber),
        );
      }
      return [{ name: fullName, phoneNumber: undefined }];
    })
    .sort(compareNames);

// Numbers duplicate name+phoneNumber entries so values stay unique without using array index.
const toSearchItems = (
  contacts: ContactInfo[],
  isCalling: boolean,
): SearchItem[] => {
  const valueOccurrences = new Map<string, number>();
  return contacts.map((contact) => {
    const baseValue = `${contact.name}-${contact.phoneNumber}`;
    const occurrence = valueOccurrences.get(baseValue) ?? 0;
    valueOccurrences.set(baseValue, occurrence + 1);
    return {
      value: occurrence === 0 ? baseValue : `${baseValue}-${occurrence}`,
      label: [contact.name, contact.phoneNumber].filter(Boolean).join(" "),
      disabled: !contact.phoneNumber || isCalling,
      name: contact.name,
      phoneNumber: contact.phoneNumber,
    };
  });
};

const CallOut = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const puzzelUserId = useUserValidationState().userId;
  const currentPuzzelProfileName = useUserDetailsState().currentProfile?.name;
  const outboundVisualPhoneNumber = outboundPhoneNumber(
    currentPuzzelProfileName,
  );
  const puzzelContext = usePuzzelAuthenticationContext();

  const { userStateInformation } = usePuzzelExtendedStateInformation();

  const [searchString, setSearchString] = useState("");
  const { currentlyCalling, setCurrentlyCalling } = usePuzzelCurrentlyCalling();

  const [catalogRawResponse, performStatefulCatalogSearch] = useApiAction(
    performPuzzelCatalogSearch,
    false,
  );
  const [performCatalogSearchWithDebounce] = useState(() =>
    debounce(performStatefulCatalogSearch, 200),
  );
  const [searchResults, setSearchResults] = useState<ContactInfo[]>([]);
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [highlightedValue, setHighlightedValue] = useState<string | null>(null);

  // Autocomplete exposes no ref for its scrollable content. Find it in our wrapper after the
  // dropdown first opens. The [data-part] selector is an internal Chakra/Ark detail.
  // useInfiniteScroll reattaches its listener when this ref object changes, so keep it tied to state.
  const [resolvedContentElement, setResolvedContentElement] =
    useState<HTMLElement | null>(null);
  const contentContainerRef = useMemo(
    () => ({ current: resolvedContentElement }),
    [resolvedContentElement],
  );

  const resetCatalogSearch = () => {
    setSearchString("");
    setSearchResults([]);
    setIsSearchPending(false);
    performCatalogSearchWithDebounce.clear();
  };

  const initiatePhoneCallWithTimeout = (numberToCall: string) => {
    initiatePhoneCall(
      numberToCall,
      // @ts-ignore
      puzzelUserId,
      puzzelContext,
      outboundVisualPhoneNumber,
    ).catch((error) => {
      log(LogLevel.error, "CallOut", "Failed to initiate phone call", error);
    });
    setTimeout(() => {
      setCurrentlyCalling(null);
    }, 3000);
  };

  const { mouseclickRef } =
    useOnOutsideMouseclick<HTMLDivElement>(resetCatalogSearch);

  const isAvailable = userStateInformation?.userStatus === UserStatus.Available;

  useEffect(() => {
    if (!isAvailable) {
      resetCatalogSearch();
      setResolvedContentElement(null);
    }
  }, [isAvailable]);

  useEffect(() => {
    if (currentlyCalling) {
      initiatePhoneCallWithTimeout(currentlyCalling);
    }
  }, [currentlyCalling]);

  useEffect(() => {
    if (catalogRawResponse.data?.result) {
      setSearchResults(assembleSearchResults(catalogRawResponse.data?.result));
      setIsSearchPending(false);
    } else if (catalogRawResponse.error) {
      setIsSearchPending(false);
    }
  }, [catalogRawResponse.data, catalogRawResponse.error]);

  useEffect(() => {
    if (searchString !== "") {
      setIsSearchPending(true);
      performCatalogSearchWithDebounce({
        searchString,
        userId: puzzelUserId,
      });
    } else {
      resetCatalogSearch();
    }
  }, [searchString]);

  let suggestionsToDisplay: ContactInfo[];
  if (validPhoneNumber(searchString)) {
    const inputPhoneNumber: ContactInfo = {
      phoneNumber: searchString,
      name: "",
    };
    suggestionsToDisplay = [inputPhoneNumber, ...searchResults];
  } else {
    suggestionsToDisplay = [...searchResults];
  }

  const isCalling = currentlyCalling !== null;
  const searchItems = toSearchItems(suggestionsToDisplay, isCalling);

  const { numberOfItemsToRender } = useInfiniteScroll({
    containerRef: contentContainerRef,
    initialItemsToRender: 25,
    maxItemsToRender: searchItems.length,
  });

  const callTopSuggestionOnEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" || highlightedValue !== null) {
      return;
    }
    event.preventDefault();
    const topItem = searchItems[0];
    if (topItem?.phoneNumber && !topItem.disabled) {
      setCurrentlyCalling(topItem.phoneNumber);
    }
  };

  let CallOutElement = null;
  if (isAvailable) {
    CallOutElement = (
      <CallOutStyled
        ref={mouseclickRef}
        onKeyDownCapture={callTopSuggestionOnEnter}
      >
        <CenteredLabelInput>
          <Autocomplete
            label={t("Hvem vil du ringe?")}
            size="sm"
            inputValue={searchString}
            onInputValueChange={(details) =>
              setSearchString(details.inputValue)
            }
            filteredExternally
            loading={isSearchPending && searchItems.length === 0}
            onHighlightChange={(details: { highlightedValue: string | null }) =>
              setHighlightedValue(details.highlightedValue)
            }
            onSelect={(details) => {
              if (!("itemValue" in details)) {
                return;
              }
              const chosen = searchItems.find(
                (item) => item.value === details.itemValue,
              );
              if (chosen?.phoneNumber) {
                setCurrentlyCalling(chosen.phoneNumber);
              }
            }}
            onOpenChange={(details) => {
              if (!details.open && details.reason === "escape-key") {
                resetCatalogSearch();
              }
              if (details.open && !resolvedContentElement) {
                // The scrollable "content" element doesn't exist in the DOM until the dropdown
                // actually opens, so it can't be found any earlier than this.
                requestAnimationFrame(() => {
                  const element = mouseclickRef.current?.querySelector(
                    '[data-part="content"]',
                  ) as HTMLElement | null;
                  if (element) {
                    setResolvedContentElement(element);
                  }
                });
              }
            }}
            css={{
              width: "250px",
              '& [data-part="item"][data-highlighted]': {
                backgroundColor: theme.colorSelected,
              },
              '& [data-part="item"], & [data-part="input"], & [data-part="label"]':
                {
                  color: theme.colorTextMain,
                },
            }}
          >
            {searchItems.slice(0, numberOfItemsToRender).map((item) => (
              <AutocompleteItem key={item.value} item={item}>
                <span>{item.label}</span>
                {item.value === highlightedValue && !item.disabled && (
                  <CheckmarkOutline18Icon />
                )}
              </AutocompleteItem>
            ))}
          </Autocomplete>
        </CenteredLabelInput>
      </CallOutStyled>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{CallOutElement}</>;
};

export default CallOut;
